import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  physiciansService,
  CreatePhysicianInput,
  UpdatePhysicianInput,
} from "../../services/admin/physicians.service";

export type { LinkedSurgeryCenter } from "../../services/admin/physicians.service";

export function usePhysiciansAdmin() {
  return useQuery({
    queryKey: ["admin", "physicians"],
    queryFn: physiciansService.getAll,
  });
}

export function usePhysician(id?: string) {
  return useQuery({
    queryKey: ["admin", "physicians", id],
    queryFn: () => physiciansService.getById(id!),
    enabled: !!id,
  });
}

export function useCreatePhysician() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreatePhysicianInput) =>
      physiciansService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "physicians"] });
      queryClient.invalidateQueries({ queryKey: ["physicians"] });
      notifications.show({
        title: "Success",
        message: "Physician created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create physician",
        color: "red",
      });
    },
  });
}

export function useUpdatePhysician() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdatePhysicianInput) =>
      physiciansService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "physicians"] });
      queryClient.invalidateQueries({ queryKey: ["physicians"] });
      notifications.show({
        title: "Success",
        message: "Physician updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update physician",
        color: "red",
      });
    },
  });
}

export function useDeletePhysician() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => physiciansService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "physicians"] });
      queryClient.invalidateQueries({ queryKey: ["physicians"] });
      notifications.show({
        title: "Success",
        message: "Physician deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete physician",
        color: "red",
      });
    },
  });
}

export function useTogglePhysicianActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      physiciansService.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "physicians"] });
      notifications.show({
        title: "Success",
        message: "Status updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update status",
        color: "red",
      });
    },
  });
}

export function usePhysicianLinkedPractices(physicianId: string | null) {
  return useQuery({
    queryKey: ["admin", "physicians", physicianId, "linked-practices"],
    queryFn: () => physiciansService.getLinkedPractices(physicianId!),
    enabled: !!physicianId,
  });
}

export function usePhysicianLinkedSurgeryCenters(physicianId: string | null) {
  return useQuery({
    queryKey: ["admin", "physicians", physicianId, "linked-surgery-centers"],
    queryFn: () => physiciansService.getLinkedSurgeryCenters(physicianId!),
    enabled: !!physicianId,
  });
}
